import { createSelector } from '@ngrx/store';
import { getAccountName, getDriverName, getRouterState } from '@appstore/*';

import { getOverviewState, OverviewState } from '../registration';
import { TourState } from '../reducers';
import { getDriverNotesKeyForTSA, isPrevDaysTour, sortTours, validateAction } from '@shared/*';
import { Depot, Driver, DriverName, DriverNote, LegacyTransportType, LocationType, Stop, Tour, TourStopAction } from '@models/*';
import { TransportPurpose } from '../../../base/entities/transport.interface';

/*
 * todo: fix naming: this started as "tours" containing all the data
 *  but was not normalized in the state (see https://redux.js.org/recipes/structuring-reducers/normalizing-state-shape for explanation)
 *  this is now going to be handled by the back-end, starting with driverNotes as separate collection
 *  the state shape, selectors, actions, reducers etc. should also gradually be adjusted as more work is done
 */
export const getToursState = createSelector(getOverviewState, (state: OverviewState) => state.tours);

export const getToursEntities = createSelector(getToursState, (state: TourState) => state.entities);
// not exported because there are also depots-selectors from separate API call
// this is normalized from getDriverData and only used in responsible branch at the moment
// todo: optimize, this can possibly only load all depots for the opco, and come from getDriverData, or stay in separate api call only
const getDepots = createSelector(getToursState, (state: TourState) => state.depots);
export const getAllDriverNotes = createSelector(getToursState, (state: TourState) => state.driverNotes);
// getAllDriverNamesForNotes adds the logged-in driver's driver name when available to be able to always use for newly created notes
// since the back-end only returns the driver names for the notes that are related to the current tours
export const getAllDriverNamesForNotes = createSelector(getToursState, getDriverName, (state: TourState, driverName) => {
    return driverName ?
        { ...state.driverNames, [driverName.id]: driverName }
        : state.driverNames;
});
// the drivers for the notes are the accounts, in case there are no multiple "driver names", use the account name to identify the note
export const getAllDriversForNotes = createSelector(getToursState, getAccountName, (state: TourState, accountName) => {
    const driverId = state.driverId;
    if (!state.drivers.hasOwnProperty(driverId)) {
        return { ...state.drivers, [driverId]: { id: driverId, name: accountName } };
    }
    return state.drivers;
});
export const getAllNotesForCounterParty = createSelector(getToursState, (state: TourState) => state.notesForCounterParty);
// TODO: this driver id is a temporary solution because it belongs in the core app store
export const getDriverId = createSelector(getToursState, (state: TourState) => state.driverId);
export const getToursLoaded = createSelector(getToursState, (state: TourState) => state.loaded);
export const getToursLoading = createSelector(getToursState, (state: TourState) => state.loading);
export const getSingleToursLoaded = createSelector(getToursState, (state: TourState) => state.singleLoaded);
export const getActionsUpdating = createSelector(getToursState, (state: TourState) => state.actionsUpdating);
export const getFoundMachines = createSelector(getToursState, (state: TourState) => state.foundMachines);
export const getMachineSearching = createSelector(getToursState, (state: TourState) => state.machineSearching);
export const getIsSwapProposal = createSelector(getToursState, (state: TourState) => state.isSwapProposal);
export const getSwapProposal = createSelector(getToursState, (state: TourState) => state.swapProposal);
export const getToursFilterOn = createSelector(getToursState, (state: TourState) => state.filterOn);

export const getTours = createSelector(getToursEntities, entities => Object.keys(entities).map(key => entities[parseInt(key, 10)]));

export const getAnyPrevDaysTour = createSelector(getTours, tours => tours.some(tour => isPrevDaysTour(tour)));

export const getFilteredTours = createSelector(getTours, getToursFilterOn, (tours, filterOn) => {
    let filteredTours;

    if (!filterOn) {
        filteredTours = tours.filter(tour => !isPrevDaysTour(tour));
    } else {
        filteredTours = tours.filter(tour => isPrevDaysTour(tour));
    }
    return sortTours(filteredTours);
});

export const getAllActions = createSelector(getTours, (tours: Tour[]) => {
    return tours
        .reduce((accum, tour: Tour) => accum.concat(tour.stops), [])
        .reduce((accum, stop: Stop) => accum.concat(stop.actions), []);
});

export const getCurrentTour = createSelector(getToursEntities, getRouterState, (entities, router) => {
    if (router && router.state && entities) {
        return router.state && entities[router.state.params.tourId];
    } else {
        return null;
    }
});

export const getCurrentStop = createSelector(getCurrentTour, getRouterState, (tour: Tour, router) => {
    if (router && router.state && tour && tour.stops && tour.stops.length) {
        return tour.stops.find((stop: Stop) => stop.id === parseInt(router.state.params.stopId, 10));
    } else {
        return null;
    }
});

export const getCurrentAction = createSelector(
    getCurrentStop,
    getRouterState,
    getAllDriverNotes,
    getAllDriverNamesForNotes,
    getAllDriversForNotes,
    getDepots,
    getAllNotesForCounterParty,
    (
        stop: Stop,
        router: any,
        allDriverNotes: { [key: string]: DriverNote[] },
        driverNames: { [key: string]: DriverName },
        drivers: { [key: string]: Driver },
        depots: { [key: string]: Depot },
        notesForCounterParty: { [tourStopActionId: number]: string },
    ) => {
        if (router && router.state && stop && stop.actions && stop.actions.length) {
            let currentAction = stop.actions.find((action: TourStopAction) => action.id === parseInt(router.state.params.actionId, 10));
            if (currentAction) {
                const driverNotesKey = getDriverNotesKeyForTSA(currentAction);
                let driverNotes = allDriverNotes[driverNotesKey] || [];
                if (driverNotes.length) {
                    driverNotes = driverNotes.map(driverNote => ({
                        ...driverNote,
                        driverName: driverNames[driverNote.driverNameId],
                        driver: drivers[driverNote.driverId],
                    }));
                }
                if (currentAction && currentAction.transport && currentAction.transport.contract &&
                    currentAction.transport.contract.responsibleBranchId) {
                    const { responsibleBranchId } = currentAction.transport.contract;
                    currentAction.transport = {
                        ...currentAction.transport,
                        contract: {
                            ...currentAction.transport.contract,
                            responsibleBranch: depots[responsibleBranchId],
                        },
                    };
                }
                const driverMessageForCounterParty = notesForCounterParty[currentAction.id];
                currentAction = {
                    ...currentAction,
                    driverNotes,
                    driverMessageForCounterParty,
                };
            }
            return currentAction;
        } else {
            return null;
        }
    },
);

export const getSingleTourLoaded = createSelector(getSingleToursLoaded, (toursLoaded: number[], tourId: number) => {
    return toursLoaded.includes(tourId);
});

export const getCurrentActionUpdating = createSelector(
    getActionsUpdating,
    getCurrentAction,
    (toursLoading: number[], action: TourStopAction) => !!action ? toursLoading.includes(action.id) : false
);

export const getCurrentStopActionsUpdating = createSelector(
    getActionsUpdating,
    getCurrentStop,
    (actionsUpdating: number[], stop: Stop) => {
        if (!stop) {
            return [];
        }
        return stop
            .actions
            .map((action: TourStopAction) => action.id)
            .filter(id => actionsUpdating.includes(id));
    });

export const getCurrentStopUpdating = createSelector(getCurrentStopActionsUpdating, actionsUpdating => !!actionsUpdating.length);

export const getCurrentLinkAction = createSelector(getCurrentAction, (action: TourStopAction) => {
    return !!action && !!action.transport && !!action.transport.machine;
});

export const getCurrentVendorAction = createSelector(
    getCurrentStop,
    getCurrentAction,
    getCurrentLinkAction,
    (stop: Stop, action: TourStopAction, isLinkAction: boolean) => {
        if (!stop || !action || isLinkAction) {
            return false;
        }
        return stop.locationType === LocationType.CUSTOMER && action.transport.purpose === TransportPurpose.SUBRENTAL;
    });

export const getCurrentActionHasChecklist = createSelector(
    getCurrentStop,
    getCurrentVendorAction,
    (stop: Stop, isVendorAction: boolean) => {
        if (!stop || isVendorAction) {
            return false;
        }
        return stop.locationType === LocationType.CUSTOMER;
    });

export const getCurrentActionHasCrosscheck = createSelector(
    getCurrentAction,
    getCurrentActionHasChecklist,
    (action: TourStopAction, hasChecklist: boolean) => {
        if (!action || !hasChecklist) {
            return false;
        }
        return action.transport.transportType === LegacyTransportType.PICKUP;
    });

export const getCurrentActionValidationResult = createSelector(
    getCurrentTour,
    getCurrentStop,
    getCurrentAction,
    (tour: Tour, stop: Stop, action: TourStopAction) => validateAction(tour, stop, action)
);
